<script setup lang="ts">
import {B2B} from "~/constants/values/client";
import {useStoreOrderComment} from "~/composables/store/order/mainForm";
import type {PropType} from "vue";
import type {AddressErrors, Coordinates} from "~/types/addressTypes";
import debounce from "lodash.debounce";
import LeafletMap from "~/components/component/leaflet-map.vue";
import {getAddressByCoords, getSuggestionByAddress} from "~/composables/external-services/useFindAddress";
import {getCityMapCenter} from "~/utils/getCityMapCenter";
import {getScrollContainer} from "~/utils/scrollToAnchor";
import {useIsHouseHomeType} from "~/composables/store/services/service_inputs";

const props = defineProps({
  // address: {
  //   type: Object as PropType<InputAddress|Address>,
  //   default: {
  //     city: {
  //       location_id: Number,
  //       title: String
  //     },
  //     street: String,
  //     house: String,
  //     block: String,
  //     entrance: String,
  //     flat: String,
  //     floor: String,
  //     latitude: String,
  //     longitude: String,
  //     has_cat: Boolean,
  //     has_dog: Boolean,
  //     has_pet: Boolean,
  //   }
  // },
  errors: {
    type: Object as PropType<AddressErrors>,
    default: {
      city: {
        isError: false,
        text: ''
      },
      street: {
        isError: false,
        text: ''
      },
      house: {
        isError: false,
        text: ''
      },
    }
  }
})
const address = defineModel()
const isMapLoading = ref(false)
const coords = ref({})
const isChangeFromCoords = ref(false)
const suggestedAddressList = ref([])
const openSuggestedAddressList = ref(false)
const { isMobile } = useDevice()
const route = useRoute()
const isBccPage = computed(() => route.name === 'bcc')
const isHouseType = useIsHouseHomeType()

const openMap = ref(false)
// карта открывалась на Алматы независимо от выбранного города
const mapCenter = ref(getCityMapCenter(address.value?.city?.title))
if (address.value?.longitude && address.value?.latitude) {
  coords.value = {
    lat: address.value?.latitude,
    lon: address.value?.longitude,
  }
}
function handleSelect(event:City) {
  const isCityChanged = address.value.city?.location_id !== event.location_id
  address.value.city=event
  useStoreUserCity().value =event
  if(props.errors && props.errors.city) {
    props.errors.city.isError = false
  }
  if (!isCityChanged) {
    return
  }
  // метка и координаты остались бы от адреса в прежнем городе, поэтому сбрасываем их
  // и переносим карту в центр нового города — дальше её подвинет геокодирование улицы
  coords.value = {}
  address.value.latitude = ''
  address.value.longitude = ''
  suggestedAddressList.value = []
  openSuggestedAddressList.value = false
  mapCenter.value = getCityMapCenter(event.title)
}
const cities = useStoreCities()

// в мини-аппе улица и номер дома — раздельные поля, и запрос в /geocode/suggest
// («улица Манаса, 34/1») бьёт мимо цели, пока заполнено только одно из них —
// поэтому геокодирование в bcc ждёт обоих полей, а не только улицы
function hasAddressPartsForGeocode() {
  if (!address.value?.street?.trim()) {
    return false
  }
  if (isBccPage.value && !address.value?.house?.trim()) {
    return false
  }
  return true
}

const handleSearchAddress = debounce(async () => {
  // без улицы (и, в bcc, без дома) геокодировать нечего: запрос вида «Астана, »
  // ничего не находит, карта должна остаться в центре выбранного города
  if (!hasAddressPartsForGeocode()) {
    return
  }
  isMapLoading.value = true
  let data = {
    street: '',
    // город берём из самого адреса, а не из глобального стора: в редактировании
    // они могут отличаться, и улица искалась бы не в том городе
    city: address.value?.city?.title
  }
  if (address.value?.street) {
    data.street = address.value?.street
  }
  if (address.value?.house) {
    data.street = data.street + ', ' + address.value?.house
  }
  const response = await getCoordsByAddress(data)
  if (response?.coords) {
    isChangeFromCoords.value = true
    address.value.latitude = response.coords.lat
    address.value.longitude = response.coords.lon
    address.value.house = response.address?.house
    coords.value = response.coords
  } else {
    console.log(response?.coords?.err)
  }
  isMapLoading.value = false
}, 500)

// Ответ приходит через debounce и сеть — к этому моменту пользователь уже может уйти
// в «Квартиру». Список идёт в потоке, поэтому раскрывать его вдогонку нельзя: поля
// уедут вниз из-под пальца. Раскрываем, только пока фокус остаётся в блоке улицы.
function isStreetFocused() {
  if (!isBccPage.value) return true
  return !!(document.activeElement as HTMLElement | null)?.closest?.('[data-street-suggest]')
}

const getSuggestion = debounce(async (openList = true) => {
  if (!hasAddressPartsForGeocode()) {
    openSuggestedAddressList.value = false
    return
  }
  let data = {
    street: '',
    city: address.value?.city?.title
  }
  if (address.value?.street) {
    data.street = address.value?.street
  }
  if (address.value?.house) {
    data.street = data.street + ' ' + address.value?.house
  }
  const response = await getSuggestionByAddress(data)
  if (response?.results) {
    if (openList && isStreetFocused()) {
      openSuggestedAddressList.value = true
    }
    suggestedAddressList.value = response?.results
  } else {
    console.log(response?.coords?.err)
  }
}, 300)

// В мини-аппе список подсказок идёт в потоке, а не абсолютным слоем. Абсолютный
// перекрывал поле «Квартира» целиком и левый край «Подъезда»: первый тап по ним
// уходил в кнопку подсказки — улица подменялась выбранным вариантом, фокус
// возвращался в поле улицы, и до «Квартиры» пользователь добирался только вторым
// тапом. Именно это выглядело как «переход на поля с задержкой».
const suggestListRef = ref<HTMLElement | null>(null)
// mt-1 у списка
const SUGGEST_LIST_GAP_PX = 4

// Список занимает место в потоке, поэтому при закрытии всё, что ниже, уезжает вверх.
// Гасим этот сдвиг прокруткой контейнера, иначе поле, по которому только что тапнули,
// прыгает из-под пальца.
function closeSuggestions() {
  if (!openSuggestedAddressList.value) return
  const list = suggestListRef.value
  const shift = list ? Math.round(list.getBoundingClientRect().height) + SUGGEST_LIST_GAP_PX : 0
  const container = shift ? getScrollContainer(list) : null
  openSuggestedAddressList.value = false
  if (!container) return
  nextTick(() => {
    container.scrollTop = Math.max(0, container.scrollTop - shift)
  })
}

// список подсказок сам по себе не закрывался: он оставался висеть над полями
// «Квартира»/«Подъезд»/«Этаж»/«Домофон», пока пользователь их заполнял
function handleClickOutsideSuggestions(event: Event) {
  if (!isBccPage.value || !openSuggestedAddressList.value) return
  const target = event.target as HTMLElement | null
  if (target?.closest?.('[data-street-suggest]')) return
  closeSuggestions()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutsideSuggestions)
  // клавиатурная кнопка «Далее» переводит фокус без клика — по клику список бы не закрылся
  document.addEventListener('focusin', handleClickOutsideSuggestions)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutsideSuggestions)
  document.removeEventListener('focusin', handleClickOutsideSuggestions)
})

// ответ /geocode/suggest приходит одной строкой вида «улица Манаса, 34/1» —
// в bcc улица и дом разнесены по разным полям, поэтому строку нужно разобрать
function splitStreetAndHouse(adr: string) {
  const separatorIndex = adr.lastIndexOf(',')
  if (separatorIndex === -1) {
    return {street: adr.trim(), house: ''}
  }
  return {
    street: adr.slice(0, separatorIndex).trim(),
    house: adr.slice(separatorIndex + 1).trim()
  }
}

function useSuggestedAddress(adr: string) {
  // подстановка из списка — не пользовательский ввод, список подсказок открывать заново не нужно
  isChangeFromCoords.value = true
  if (isBccPage.value) {
    const {street, house} = splitStreetAndHouse(adr)
    address.value.street = street
    address.value.house = house
  } else {
    address.value.street = adr
  }
  closeSuggestions()
  handleSearchAddress()
}

// следим только за полями, от которых зависит геокодирование: иначе ввод квартиры,
// подъезда, этажа или домофона тоже открывал бы список подсказок по улице
watch(() => [address.value?.city?.location_id, address.value?.street, address.value?.house], async () => {
  if (!isChangeFromCoords.value) {
    await handleSearchAddress()
    await getSuggestion()
  } else {
    await getSuggestion(false)
  }
}, {immediate: true})

function switchHasPet(type: 'dog' | 'cat' | 'pet') {
  switch (type) {
    case 'dog':
      address.value.has_dog = !address.value?.has_dog
      break
    case 'cat':
      address.value.has_cat = !address.value?.has_cat
      break
    case 'pet':
      address.value.has_pet = !address.value?.has_pet
      break
  }
}

async function fillAddress(coordsOpt: Coordinates) {
  isMapLoading.value = true
  const res = await getAddressByCoords({coords: coordsOpt})
  isChangeFromCoords.value = true
  const house = res?.house?.trim() ?? ''
  const street = res?.street ?? ''
  if (isBccPage.value) {
    // в bcc дом — отдельное поле, не нужно склеивать его со строкой улицы
    address.value = {...address.value, street, house}
  } else {
    const streetHasHouse = house && street.split(/\s+/).includes(house)
    address.value = {
      ...address.value,
      street: streetHasHouse ? street : [street, house].filter(Boolean).join(' '),
      house,
    }
  }
  coords.value = coordsOpt
  isMapLoading.value = false
}
</script>

<template>
  <div>
    <div v-if="!isMobile && !isBccPage" class="mb-6">
      <client-only>
        <div class="overflow-hidden rounded-3xl">
          <leaflet-map v-model="coords" @get-coords="fillAddress" :loading="isMapLoading" :center="mapCenter"/>
        </div>
      </client-only>
    </div>
    <div class="mb-3">
      <div v-if="!isBccPage" class="flex md:flex-row flex-col gap-3">
        <div class="flex flex-col gap-2">
          <div class="flex flex-row gap-2">
            <label v-if="errors?.city?.isError" class="order-service-form-error">
              {{ errors.city.text }}
            </label>

            <label v-else class="order-service-form-text">
              {{ $t('input_forms.field.city.title') }}
            </label>
            <p class=" text-red-600 text-xs">*</p>
          </div>
          <UIDropdown
              @select="handleSelect"
              :list="cities"
              :key_field="'location_id'"
              :title_field="['title']"
              :placeholder="address.city?.location_id ? address.city.title : $t('input_forms.field.city.placeholder')"
              class="rounded-[10px] z-[5]"
              :class="{ 'border border-primary-red ' : errors?.city?.isError }"
          />
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-row gap-2">
            <label v-if="errors?.street?.isError" class="order-service-form-error">
              {{ errors.street.text }}</label>
            <label v-else class="order-service-form-text">
              {{ $t("input_forms.field.address.title.street") }}</label>
            <p class=" text-red-600 text-xs">*</p>
          </div>
          <div class="relative">
            <div
                class="items-center mb-2 rounded-lg flex bg-white"
                :class="{ 'border border-secondary-600' : !errors?.street?.isError,
                           'border border-primary-red' : errors?.street?.isError}"
            >
              <UIInput
                  type="text"
                  class="bg-secondary rounded-lg border-none pr-0"
                  v-model="address.street"
                  :placeholder="$t('order.location.street_and_house_placeholder')"
                  @input="errors?.street?.isError ? errors.street.isError = false : ''; isChangeFromCoords = false"
              />
              <button
                  :disabled="!suggestedAddressList.length"
                  @click="openSuggestedAddressList = !openSuggestedAddressList"
                  type="button"
                  class="flex items-center justify-center p-3"
              >
                <IconUIChevronUp class="transition-all duration-150 ease-in" :class="{ 'flip': !openSuggestedAddressList }"/>
              </button>
            </div>
            <div v-if="openSuggestedAddressList"
                 class="absolute top-[100%] w-full max-h-[120px] z-10 overflow-y-auto bg-secondary border border-secondary-600 rounded-lg">
              <button
                  type="button"
                  v-for="item in suggestedAddressList"
                  class="border-b border-secondary-600 p-2 w-full text-left text-sm"
                  @click="useSuggestedAddress(item.title.text)"
              >
                {{item.title.text}}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="flex flex-col gap-4">
        <div>
          <div class="text-[13px] font-semibold text-[#2c3530] mb-[9px]">{{ $t('input_forms.field.city.title') }} <span class="text-red-600">*</span></div>
          <div class="flex gap-2 flex-wrap">
            <button
                v-for="city in cities"
                :key="city.location_id"
                type="button"
                @click="handleSelect(city)"
                class="py-[11px] px-[15px] rounded-full border-[1.5px] text-[13px] transition-colors"
                :class="address.city?.location_id === city.location_id
                  ? 'border-primary-green bg-primary-green bg-opacity-10 text-primary-green font-bold'
                  : 'border-[#E8EAE8] bg-white text-[#2c3530] font-semibold'"
            >{{ city.title }}</button>
          </div>
          <p v-if="errors?.city?.isError" class="text-xs text-primary-red mt-1 pl-2">{{ errors.city.text }}</p>
        </div>

        <!-- список подсказок вынесен из <label> и из абсолютного позиционирования:
             внутри label клик по подсказке дополнительно возвращал фокус в поле улицы -->
        <div data-street-suggest>
          <label class="block">
            <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.street') }} <span class="text-red-600">*</span></span>
            <div class="flex gap-2">
              <div class="flex-1 min-w-0">
                <UIInput
                    type="text"
                    v-model="address.street"
                    :placeholder="$t('bcc.steps.address.field.street_placeholder')"
                    class="rounded-[14px] bg-white border-[1.5px] h-[50px] text-[15px] text-[#16211C]"
                    :class="errors?.street?.isError ? 'border-primary-red' : 'border-[#E8EAE8]'"
                    @input="errors?.street?.isError ? errors.street.isError = false : ''; isChangeFromCoords = false"
                />
              </div>
              <button
                  type="button"
                  @click="openMap = true"
                  class="flex-none h-[50px] px-[15px] rounded-[14px] border-[1.5px] border-primary-green bg-primary-green bg-opacity-[0.08] text-primary-green text-[13px] font-bold flex items-center gap-[6px]"
              >
                <IconUIFilledLocation class="w-3.5 h-3.5"/>
                {{ $t('order.location.on_map') }}
              </button>
            </div>
          </label>
          <p v-if="errors?.street?.isError" class="text-xs text-primary-red mt-1 pl-2">{{ errors.street.text }}</p>

          <div v-if="openSuggestedAddressList && suggestedAddressList.length"
               ref="suggestListRef"
               class="mt-1 max-h-[160px] overflow-y-auto bg-white border-[1.5px] border-[#E8EAE8] rounded-[14px] shadow-sm">
            <button
                type="button"
                v-for="item in suggestedAddressList"
                class="border-b border-[#F2F1EC] last:border-b-0 p-3 w-full text-left text-[13px] text-[#16211C]"
                @click="useSuggestedAddress(item.title.text)"
            >
              {{item.title.text}}
            </button>
          </div>
        </div>
      </div>

      <UIBtn
          v-if="isMobile && !isBccPage"
          @click="openMap = true"
          border="green"
          type="button"
      >
        {{ $t('order.location.show_in_map') }}
      </UIBtn>

      <ModalBackDrop :open-modal="openMap"/>
      <ModalDownUpModal :open-modal="openMap">
        <div class="flex flex-col h-[calc(100vh-70px)]">
          <div class="p-5 pb-3 flex items-center gap-5 shrink-0">
            <UIBtn
                border="green"
                class="w-8 h-8 p-0 rounded-full flex items-center justify-center"
                type="button"
                @click="openMap = false"
            >
              <IconUIChevronUp class="rotate-[-90deg] w-4 h-4"/>
            </UIBtn>
            <p class="text-base font-bold">{{$t('order.location.show_in_map')}}</p>
          </div>

          <div class="flex-1 min-h-0 px-5 overflow-hidden">
            <div class="overflow-hidden rounded-3xl relative h-full">
              <client-only>
                <leaflet-map
                    v-model="coords"
                    @get-coords="fillAddress"
                    :loading="isMapLoading"
                    :center="mapCenter"
                    class="h-full"
                />
              </client-only>

              <div class="px-5 absolute top-[20px] w-full">
                <div data-street-suggest class="relative">
                  <div
                      class="items-center mb-2 rounded-lg flex bg-white"
                      :class="{ 'border border-secondary-600' : !errors?.street?.isError,
                             'border border-primary-red' : errors?.street?.isError}"
                  >
                    <UIInput
                        type="text"
                        class="bg-secondary rounded-lg border-none pr-0"
                        v-model="address.street"
                        :placeholder="$t('order.location.street_and_house_placeholder')"
                        @input="errors?.street?.isError ? errors.street.isError = false : ''; isChangeFromCoords = false"
                    />
                    <button
                        :disabled="!suggestedAddressList.length"
                        @click="openSuggestedAddressList = !openSuggestedAddressList"
                        type="button"
                        class="flex items-center justify-center p-3"
                    >
                      <IconUIChevronUp class="transition-all duration-150 ease-in" :class="{ 'flip': !openSuggestedAddressList }"/>
                    </button>
                  </div>
                  <div v-if="openSuggestedAddressList"
                       class="absolute top-[100%] w-full max-h-[120px] z-10 overflow-y-auto bg-secondary border border-secondary-600 rounded-lg">
                    <button
                        type="button"
                        v-for="item in suggestedAddressList"
                        class="border-b border-secondary-600 p-2 w-full text-left text-sm"
                        @click="useSuggestedAddress(item.title.text)"
                    >
                      {{item.title.text}}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-5 pt-3 shrink-0">
            <UIBtn
                @click="openMap = false"
                color="green"
                class="p-4 w-full"
                type="button"
            >
              {{ $t('input_forms.button.confirm') }}
            </UIBtn>
          </div>
        </div>
      </ModalDownUpModal>
    </div>
    <!-- все поля адреса намеренно type="text": в мини-аппе number открывал отдельную
         цифровую клавиатуру, из-за чего она менялась при переходе между полями,
         и не давал ввести «12А» в квартиру или «45К» в домофон -->
    <div v-if="isBccPage" class="flex flex-col gap-4">
      <div class="flex gap-3">
        <label class="flex-1 min-w-0">
          <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.house') }} <span class="text-red-600">*</span></span>
          <UIInput
              v-model="address.house"
              type="text"
              :placeholder="$t('bcc.steps.address.field.house_placeholder')"
              class="rounded-[14px] bg-white border-[1.5px] h-[50px] text-[15px] text-[#16211C]"
              :class="errors?.house?.isError ? 'border-primary-red' : 'border-[#E8EAE8]'"
              @input="errors?.house?.isError ? errors.house.isError = false : ''; isChangeFromCoords = false"
          />
          <p v-if="errors?.house?.isError" class="text-xs text-primary-red mt-1 pl-2">{{ errors.house.text }}</p>
        </label>
        <label v-if="!isHouseType" class="flex-1 min-w-0">
          <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.flat') }} <span class="text-red-600">*</span></span>
          <UIInput
              v-model="address.flat"
              type="text"
              :placeholder="$t('bcc.steps.address.field.flat_placeholder')"
              class="rounded-[14px] bg-white border-[1.5px] h-[50px] text-[15px] text-[#16211C]"
              :class="errors?.flat?.isError ? 'border-primary-red' : 'border-[#E8EAE8]'"
              @input="errors && errors.flat ? errors.flat.isError = false : ''"
          />
          <p v-if="errors?.flat?.isError" class="text-xs text-primary-red mt-1 pl-2">{{ errors.flat.text }}</p>
        </label>
      </div>
      <div v-if="!isHouseType" class="flex gap-3">
        <label class="flex-1 min-w-0">
          <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.entrance') }} <span class="text-red-600">*</span></span>
          <UIInput
              v-model="address.entrance"
              type="text"
              @input="isChangeFromCoords = false; errors && errors.entrance ? errors.entrance.isError = false : ''"
              :placeholder="$t('bcc.steps.address.field.entrance_placeholder')"
              class="rounded-[14px] bg-white border-[1.5px] h-[50px] text-[15px] text-[#16211C]"
              :class="errors?.entrance?.isError ? 'border-primary-red' : 'border-[#E8EAE8]'"
          />
          <p v-if="errors?.entrance?.isError" class="text-xs text-primary-red mt-1 pl-2">{{ errors.entrance.text }}</p>
        </label>
        <label class="flex-1 min-w-0">
          <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.floor') }} <span class="text-red-600">*</span></span>
          <UIInput
              v-model="address.floor"
              type="text"
              :placeholder="$t('bcc.steps.address.field.floor_placeholder')"
              class="rounded-[14px] bg-white border-[1.5px] h-[50px] text-[15px] text-[#16211C]"
              :class="errors?.floor?.isError ? 'border-primary-red' : 'border-[#E8EAE8]'"
              @input="errors && errors.floor ? errors.floor.isError = false : ''"
          />
          <p v-if="errors?.floor?.isError" class="text-xs text-primary-red mt-1 pl-2">{{ errors.floor.text }}</p>
        </label>
      </div>
      <div v-if="!isHouseType" class="flex gap-3">
        <label class="flex-1 min-w-0">
          <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.doorphone') }}</span>
          <UIInput
              v-model="address.doorphone"
              type="text"
              :placeholder="$t('bcc.steps.address.field.doorphone_placeholder')"
              class="rounded-[14px] bg-white border-[1.5px] border-[#E8EAE8] h-[50px] text-[15px] text-[#16211C]"
          />
        </label>
      </div>
      <div>
        <div class="text-[13px] font-semibold text-[#2c3530] mb-[9px]">{{ $t('input_forms.pets.what_pets') }}</div>
        <div class="flex gap-2 flex-wrap">
          <button
              type="button"
              @click="switchHasPet('dog')"
              class="flex items-center gap-2 py-[11px] px-[15px] rounded-full border-[1.5px] text-[13px] transition-colors"
              :class="address.has_dog
                ? 'border-primary-green bg-primary-green bg-opacity-10 text-primary-green font-bold'
                : 'border-[#E8EAE8] bg-white text-[#2c3530] font-semibold'"
          >
            {{ $t('input_forms.pets.dogs') }}
          </button>
          <button
              type="button"
              @click="switchHasPet('cat')"
              class="flex items-center gap-2 py-[11px] px-[15px] rounded-full border-[1.5px] text-[13px] transition-colors"
              :class="address.has_cat
                ? 'border-primary-green bg-primary-green bg-opacity-10 text-primary-green font-bold'
                : 'border-[#E8EAE8] bg-white text-[#2c3530] font-semibold'"
          >
            {{ $t('input_forms.pets.cats') }}
          </button>
          <button
              type="button"
              @click="switchHasPet('pet')"
              class="flex items-center gap-2 py-[11px] px-[15px] rounded-full border-[1.5px] text-[13px] transition-colors"
              :class="address.has_pet
                ? 'border-primary-green bg-primary-green bg-opacity-10 text-primary-green font-bold'
                : 'border-[#E8EAE8] bg-white text-[#2c3530] font-semibold'"
          >
            {{ $t('input_forms.pets.pets') }}
          </button>
        </div>
      </div>
      <!--
      <label class="block">
        <span class="text-[13px] font-semibold text-[#2c3530] block mb-[7px]">{{ $t('bcc.steps.address.field.notes') }}</span>
        <UIInput
            v-model="address.notes"
            type="text"
            :placeholder="$t('bcc.steps.address.field.notes_placeholder')"
            class="rounded-[14px] bg-white border-[1.5px] border-[#E8EAE8] h-[50px] text-[15px] text-[#16211C]"
        />
      </label>
      -->
    </div>
    <div v-else class="flex flex-col gap-2 ">
      <div class="grid md:grid-cols-3 grid-cols-4 md:gap-3 gap-2">
        <label for="order-client-office">
          <p class="py-2 order-service-form-text">{{ $t('order.location.block') }}</p>

          <UIInput
              v-model="address.block"
              @input="isChangeFromCoords = false"
              :id="'order-client-office'"
              placeholder="00"
              class="bg-secondary rounded-lg border-secondary-600"
          />

        </label>

        <label for="order-client-entrance" class="flex flex-col justify-between">
        <span class="flex flex-row gap-2 items-center">
          <p class="py-2 order-service-form-text">{{ $t('order.location.entrance') }}</p>
          </span>
          <UIInput
              v-model="address.entrance"
              @input="isChangeFromCoords = false"
              type="number"
              :id="'order-client-entrance'"
              placeholder="00"
              class="bg-secondary rounded-lg border-secondary-600 block mt-auto"
          />
        </label>
        <label for="order-client-floor" class="flex flex-col justify-between">
    <span class="flex flex-row justify-start gap-2 items-center">
          <p class="py-2 order-service-form-text">{{ $t('order.location.floor') }}</p>
    </span>
          <UIInput
              v-model="address.floor"
              type="number"
              :id="'order-client-floor'"
              placeholder="00"
              class="bg-secondary rounded-lg border-secondary-600"
          />
        </label>
        <label for="order-client-apartment" class="flex flex-col justify-between">
                  <span class="flex flex-row gap-2 items-center">
                      <p v-if="useClientTypeStore().value===B2B"
                         class="py-2 order-service-form-text">{{ $t('order.location.block') }}</p>
          <p v-else class="py-2 order-service-form-text">{{ $t('order.location.apartment') }}</p>
          </span>
          <UIInput v-if="useClientTypeStore().value===B2B" v-model="address.block"
                   :id="'order-client-apartment'"
                   placeholder="00"
                   class="bg-secondary rounded-lg border-secondary-600"/>
          <UIInput v-else
                   v-model="address.flat"
                   type="number"
                   placeholder="00"
                   :id="'order-client-apartment'"
                   class="bg-secondary rounded-lg border-secondary-600"/>
        </label>
      </div>
      <div class="flex flex-row items-end gap-3">
        <div class="w-full">
          <label for="order-client-address">
            <p class="py-2 order-service-form-text">{{ $t('order.location.address_additional') }}</p>
            <UIInput
                v-model="useStoreOrderComment().value"
                type="text"
                :id="'order-client-address'"
                :placeholder="$t('order.location.address_additional_placeholder')"
                class="bg-secondary rounded-lg border-secondary-600"
            />
          </label>
        </div>
      </div>
      <div>
        <p class="py-2 order-service-form-text">{{ $t('input_forms.pets.what_pets') }}</p>
        <div class="grid grid-cols-3 md:gap-3 gap-2">
          <button
              type="button"
              @click="switchHasPet('dog')"
              class="md:p-3 p-1 rounded-md shadow-md font-semibold flex items-center justify-center gap-3"
              :class="{ 'bg-primary-green bg-opacity-5 border border-primary-green' : address.has_dog, 'bg-white border border-secondary-400' : !address.has_dog }"
          >
            <div class="flex items-center text-sm md:text-base md:justify-center justify-center md:gap-3 gap-2 w-[130px]">
              <IconDog class="text-primary-green md:w-7 md:h-7 w-6 h-6"/>
              {{ $t('input_forms.pets.dogs') }}
            </div>
          </button>
          <button
              type="button"
              @click="switchHasPet('cat')"
              class="md:p-3 p-1 rounded-md shadow-md font-semibold flex items-center justify-center gap-3"
              :class="{ 'bg-primary-green bg-opacity-5 border border-primary-green' : address.has_cat, 'bg-white border border-secondary-400'  : !address.has_cat }"
          >
            <div class="flex items-center text-sm md:text-base md:justify-center justify-center md:gap-3 gap-2 w-[130px]">
              <IconCat class="text-primary-green md:w-7 md:h-7 w-6 h-6"/>
              {{ $t('input_forms.pets.cats') }}
            </div>
          </button>
          <button
              type="button"
              @click="switchHasPet('pet')"
              class="md:p-3 p-1 rounded-md shadow-md font-semibold flex items-center justify-center gap-3"
              :class="{ 'bg-primary-green bg-opacity-5 border border-primary-green' : address.has_pet, 'bg-white border border-secondary-400'  : !address.has_pet }"
          >
            <div class="flex items-center text-sm md:text-base md:justify-center justify-center md:gap-3 gap-2 w-[130px]">
              <IconPet class="text-primary-green md:w-7 md:h-7 w-6 h-6"/>

              {{ $t('input_forms.pets.pets') }}
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>